import {
    getCbarMultiSelectIdsFromElement,
    isCbarMultiSelect,
    type CbarMultiSelectElement,
} from '../cbar-select-bridge';

/** Chip/item shape used by invoice tags (`value`) and report currency (`title`). */
type CbarMultiSelectItem = {
    id: string;
    value: string;
    title: string;
};

function toMultiSelectItem(id: string, label: string, title?: string): CbarMultiSelectItem {
    const text = label || title || id;
    return {
        id,
        value: text,
        // Keep both keys: display-field="value" (tags) and display-field="title" (reports).
        title: title || text,
    };
}

const CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR = 'data-cbar-multi-select-submit-name';
const CBAR_MULTI_SELECT_HIDDEN_ATTR = 'data-cbar-multi-select-field';
const CBAR_MULTI_SELECT_HYDRATED_ATTR = 'data-cbar-multi-select-hydrated';
const CBAR_MULTI_SELECT_VALUE_SYNC_ATTR = 'data-cbar-multi-select-value-sync';
const CBAR_MULTI_SELECT_ITEMS_MANAGED_ATTR = 'data-cbar-multi-select-items-managed';
const CBAR_MULTI_SELECT_INITIAL_ITEMS_ATTR = 'data-cbar-multi-select-initial-items';
const CBAR_MULTI_SELECT_FILTER_CLOSE_ATTR = 'data-cbar-multi-select-filter-close';
const CBAR_MULTI_SELECT_CHIP_REMOVE_ATTR = 'data-cbar-multi-select-chip-remove';

type LitElementLike = HTMLElement & {updateComplete?: Promise<boolean>};
type CbarMultiSelectDropdownHost = HTMLElement & {_dropdownOpen?: boolean};

let multiSelectDismissWired = false;

function escapeSelectorValue(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }

    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function normalizeMultiSelectItemEntry(entry: unknown): CbarMultiSelectItem | null {
    if (typeof entry === 'object' && entry !== null) {
        const record = entry as Record<string, unknown>;
        const id = String(record.id ?? '');
        if (id === '') {
            return null;
        }

        const label = String(record.value ?? record.title ?? id);
        const title = String(record.title ?? record.value ?? label);
        return toMultiSelectItem(id, label, title);
    }

    const text = String(entry ?? '').trim();
    if (text === '') {
        return null;
    }

    return toMultiSelectItem(text, text);
}

function mergeMultiSelectItemsById(
    ...lists: Array<CbarMultiSelectItem[]>
): CbarMultiSelectItem[] {
    const merged = new Map<string, CbarMultiSelectItem>();

    for (const list of lists) {
        for (const item of list) {
            merged.set(String(item.id), toMultiSelectItem(
                String(item.id),
                String(item.value ?? item.title ?? item.id),
                String(item.title ?? item.value ?? item.id),
            ));
        }
    }

    return Array.from(merged.values());
}

function parseMultiSelectItemsJson(raw: string): CbarMultiSelectItem[] {
    const attr = raw.trim();
    if (!attr) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(attr);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((entry) => normalizeMultiSelectItemEntry(entry))
            .filter((entry): entry is CbarMultiSelectItem => entry !== null);
    } catch {
        return [];
    }
}

function parseMultiSelectItemsAttribute(element: CbarMultiSelectElement): CbarMultiSelectItem[] {
    return parseMultiSelectItemsJson(element.getAttribute('items') ?? '');
}

function readCachedInitialMultiSelectItems(element: CbarMultiSelectElement): CbarMultiSelectItem[] {
    return parseMultiSelectItemsJson(element.getAttribute(CBAR_MULTI_SELECT_INITIAL_ITEMS_ATTR) ?? '');
}

/** Snapshot server-rendered items before Lit upgrade can shrink the property. */
export function ensureInitialMultiSelectItemsCached(element: CbarMultiSelectElement): void {
    if (element.hasAttribute(CBAR_MULTI_SELECT_ITEMS_MANAGED_ATTR)) {
        return;
    }

    const cached = readCachedInitialMultiSelectItems(element);
    const fromAttribute = parseMultiSelectItemsAttribute(element);
    const merged = mergeMultiSelectItemsById(cached, fromAttribute);

    if (merged.length === 0) {
        return;
    }

    // Never shrink the snapshot — Lit may reflect only selected chips into items.
    if (cached.length > 0 && merged.length <= cached.length) {
        return;
    }

    element.setAttribute(CBAR_MULTI_SELECT_INITIAL_ITEMS_ATTR, JSON.stringify(merged));
}

export function cacheCbarMultiSelectItemsAttributesIn(root: ParentNode = document): void {
    root.querySelectorAll('cbar-multi-select-combo-box, cbar-multi-select').forEach((node) => {
        if (isCbarMultiSelect(node)) {
            ensureInitialMultiSelectItemsCached(node);
        }
    });
}

function normalizeMultiSelectItemsProperty(
    items: Array<{id: string; value: string; title?: string}> | undefined,
): CbarMultiSelectItem[] {
    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    return items.map((item) => {
        const label = String(item.value ?? item.title ?? '');
        return toMultiSelectItem(String(item.id), label, String(item.title ?? label));
    });
}

function shouldReplaceMultiSelectItems(
    current: CbarMultiSelectItem[],
    merged: CbarMultiSelectItem[],
): boolean {
    if (merged.length === 0) {
        return false;
    }

    if (current.length === 0) {
        return true;
    }

    const currentIds = new Set(current.map((item) => String(item.id)));
    if (merged.length !== current.length) {
        return true;
    }

    return merged.some((item) => !currentIds.has(String(item.id)));
}

/** Reconcile items property with the server-rendered items attribute after Lit upgrade. */
export function syncCbarMultiSelectItemsFromAttribute(element: CbarMultiSelectElement): void {
    if (element.hasAttribute(CBAR_MULTI_SELECT_ITEMS_MANAGED_ATTR)) {
        return;
    }

    ensureInitialMultiSelectItemsCached(element);

    const merged = readInitialMultiSelectItems(element);
    if (merged.length === 0) {
        return;
    }

    const current = normalizeMultiSelectItemsProperty(element.items);
    if (shouldReplaceMultiSelectItems(current, merged)) {
        element.items = merged;
    }
}

function normalizeMultiSelectValueEntry(
    entry: unknown,
    items: CbarMultiSelectItem[],
): CbarMultiSelectItem {
    if (typeof entry === 'object' && entry !== null) {
        const record = entry as Record<string, unknown>;
        const id = String(record.id ?? '');
        const label = String(record.value ?? record.title ?? id);
        const title = String(record.title ?? record.value ?? label);
        if (id !== '') {
            const match = items.find((item) => String(item.id) === id);
            return match ?? toMultiSelectItem(id, label, title);
        }
    }

    const text = String(entry ?? '').trim();
    if (text.startsWith('{')) {
        try {
            const parsed = JSON.parse(text) as Record<string, unknown>;
            if (parsed && typeof parsed === 'object' && parsed.id != null) {
                return normalizeMultiSelectValueEntry(parsed, items);
            }
        } catch {
            // fall through
        }
    }

    const match = items.find(
        (item) => String(item.id) === text
            || String(item.value) === text
            || String(item.title) === text,
    );
    return match ?? toMultiSelectItem(text, text);
}

function readInitialMultiSelectValue(element: CbarMultiSelectElement): CbarMultiSelectItem[] {
    const attr = element.getAttribute('value')?.trim() ?? '';
    if (!attr) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(attr);
        if (!Array.isArray(parsed)) {
            return [];
        }

        const items = readInitialMultiSelectItems(element);
        return parsed
            .map((entry) => normalizeMultiSelectValueEntry(entry, items))
            .filter((entry) => entry.id !== '' || entry.value !== '');
    } catch {
        return [];
    }
}

function readInitialMultiSelectItems(element: CbarMultiSelectElement): CbarMultiSelectItem[] {
    const fromCached = readCachedInitialMultiSelectItems(element);
    const fromAttribute = parseMultiSelectItemsAttribute(element);
    const fromProperty = normalizeMultiSelectItemsProperty(element.items);

    // Prefer the server-rendered items list; the property may only contain selected chips.
    return mergeMultiSelectItemsById(fromCached, fromAttribute, fromProperty);
}

/** Mark items as managed by page logic so server-rendered attribute sync is skipped. */
export function markCbarMultiSelectItemsManaged(
    element: CbarMultiSelectElement,
    items: Array<{id: string; value?: string; title?: string}>,
): void {
    const normalized = items.map((item) => {
        const label = String(item.value ?? item.title ?? item.id);
        return toMultiSelectItem(String(item.id), label, String(item.title ?? label));
    });
    element.setAttribute(CBAR_MULTI_SELECT_ITEMS_MANAGED_ATTR, '');
    element.setAttribute('items', JSON.stringify(normalized));
    element.setAttribute(CBAR_MULTI_SELECT_INITIAL_ITEMS_ATTR, JSON.stringify(normalized));
}

function filterMultiSelectValueToItems(
    value: CbarMultiSelectItem[],
    items: CbarMultiSelectItem[],
): CbarMultiSelectItem[] {
    const itemIds = new Set(items.map((item) => String(item.id)));
    return value.filter((entry) => itemIds.has(String(entry.id)));
}

/**
 * Keep the `value` attribute in sync with the live property.
 *
 * Important: do **not** `removeAttribute('value')`. Lit's Array converter
 * assigns `null` when the attribute is removed, and
 * `cbar-multi-select-combo-box` render then crashes on `this.value.length`
 * (which aborted invoice-list init whenever a status chip was present).
 *
 * Encode both `value` and `title` so Lit attribute round-trips keep chip labels
 * for display-field="value" (tags) and display-field="title" (report currency).
 */
function syncCbarMultiSelectValueAttribute(element: CbarMultiSelectElement): void {
    const current = Array.isArray(element.value) ? element.value : [];
    const normalized = current.map((entry) => {
        if (entry && typeof entry === 'object') {
            const record = entry as Record<string, unknown>;
            const id = String(record.id ?? '');
            const label = String(record.value ?? record.title ?? record.id ?? '');
            const title = String(record.title ?? record.value ?? label);
            return toMultiSelectItem(id, label, title);
        }
        const text = String(entry ?? '');
        return toMultiSelectItem(text, text);
    });
    const encoded = JSON.stringify(normalized);

    if (element.getAttribute('value') !== encoded) {
        element.setAttribute('value', encoded);
    }

    // Keep the live property aligned with the attribute shape (title + value).
    element.value = normalized;
}

function isFilterFormMultiSelect(element: Element): boolean {
    if (typeof element.closest !== 'function') {
        return false;
    }
    return !!element.closest('form.cbar-filter-form, form[name="filter_form"]');
}

export function closeCbarMultiSelectDropdown(element: HTMLElement): void {
    const host = element as CbarMultiSelectDropdownHost;
    host._dropdownOpen = false;
    host.removeAttribute('_dropdown-open');
}

function isNodeInsideHost(host: HTMLElement, node: EventTarget | null): boolean {
    if (!(node instanceof Node)) {
        return false;
    }

    let current: Node | null = node;
    while (current) {
        if (current === host) {
            return true;
        }
        if (current instanceof ShadowRoot) {
            current = current.host;
            continue;
        }
        current = current.parentNode;
    }

    return false;
}

function pathIncludesDropdownItem(path: EventTarget[]): boolean {
    return path.some(
        (entry) => entry instanceof Element && entry.classList.contains('dropdown-item'),
    );
}

function peekElementUnderDropdown(
    host: HTMLElement,
    clientX: number,
    clientY: number,
): Element | null {
    const dropdown = host.shadowRoot?.querySelector('.dropdown');
    if (!(dropdown instanceof HTMLElement)) {
        return document.elementFromPoint(clientX, clientY);
    }

    const previous = dropdown.style.pointerEvents;
    dropdown.style.pointerEvents = 'none';
    const under = document.elementFromPoint(clientX, clientY);
    dropdown.style.pointerEvents = previous;
    return under;
}

function findOpenMultiSelectHosts(): CbarMultiSelectDropdownHost[] {
    return Array.from(
        document.querySelectorAll(
            'cbar-multi-select-combo-box[_dropdown-open], cbar-multi-select[_dropdown-open]',
        ),
    ) as CbarMultiSelectDropdownHost[];
}

/**
 * Status (and other filter multi-selects) keep their absolute dropdown open after
 * a chip is added. With filter-card overflow:visible that menu paints over
 * Invoice Item / Filter and swallows the click — so the form never submits.
 */
export function setupExclusiveMultiSelectDismiss(): void {
    if (multiSelectDismissWired) {
        return;
    }
    multiSelectDismissWired = true;

    document.addEventListener(
        'pointerdown',
        (event) => {
            if (!(event instanceof PointerEvent) || event.button !== 0) {
                return;
            }

            const openHosts = findOpenMultiSelectHosts();
            if (openHosts.length === 0) {
                return;
            }

            const path = event.composedPath();
            const openInPath = openHosts.find((host) => path.includes(host)) ?? null;

            if (openInPath) {
                // Real option clicks must still select; only retarget when the
                // open panel is covering another control (empty chrome / gap).
                if (pathIncludesDropdownItem(path)) {
                    return;
                }

                const under = peekElementUnderDropdown(
                    openInPath,
                    event.clientX,
                    event.clientY,
                );
                if (under && !isNodeInsideHost(openInPath, under)) {
                    closeCbarMultiSelectDropdown(openInPath);
                    event.preventDefault();
                    event.stopPropagation();
                    queueMicrotask(() => {
                        if (under instanceof HTMLElement) {
                            under.click();
                        }
                    });
                }
                return;
            }

            openHosts.forEach((host) => {
                if (!path.includes(host)) {
                    closeCbarMultiSelectDropdown(host);
                }
            });
        },
        true,
    );
}

/** In filter bars, close after each chip so the menu cannot cover Filter. */
function wireFilterMultiSelectCloseOnSelect(element: CbarMultiSelectElement): void {
    if (element.hasAttribute(CBAR_MULTI_SELECT_FILTER_CLOSE_ATTR)) {
        return;
    }
    if (!isFilterFormMultiSelect(element)) {
        return;
    }

    element.setAttribute(CBAR_MULTI_SELECT_FILTER_CLOSE_ATTR, '');
    element.addEventListener('value-changed', () => {
        // _onItemClick re-focuses the input and reopens the menu; close after that
        // and leave the field blurred so Filter/Clear are not covered.
        window.setTimeout(() => {
            closeCbarMultiSelectDropdown(element);
            const input = element.shadowRoot?.querySelector('#input');
            if (input instanceof HTMLElement && typeof input.blur === 'function') {
                input.blur();
            }
        }, 0);
    });
}

/**
 * Patch removeItem so chip dismiss compares search-prop with string coercion.
 * Stock 5.0.10 uses `===`, so hydrated string ids vs numeric item ids leave the
 * old status selected — Filter then submits old + new (`status[]=0&status[]=7`).
 */
function patchCbarMultiSelectRemoveItem(element: CbarMultiSelectElement): void {
    if (element._cbarRemoveItemPatched) {
        return;
    }
    if (typeof element.removeItem !== 'function') {
        return;
    }

    element._cbarRemoveItemPatched = true;
    element.removeItem = (item: unknown) => {
        const searchProp = element.searchProp || element.getAttribute('search-prop') || 'id';
        const selected = Array.isArray(element.value) ? element.value : [];
        element.value = selected.filter((entry) => {
            if (
                entry &&
                typeof entry === 'object' &&
                item &&
                typeof item === 'object'
            ) {
                const left = (entry as Record<string, unknown>)[searchProp];
                const right = (item as Record<string, unknown>)[searchProp];
                return String(left) !== String(right);
            }
            return entry !== item;
        });
        element._updateAvailableItems?.();
        element._dispatchValueChanged?.();
    };
}

function resolveMultiSelectEntryId(
    entry: unknown,
    searchProp: string,
): string {
    if (entry && typeof entry === 'object') {
        const record = entry as Record<string, unknown>;
        const raw = record[searchProp] ?? record.id;
        return raw != null && raw !== '' ? String(raw) : '';
    }
    return String(entry ?? '');
}

function writeMultiSelectIds(element: CbarMultiSelectElement, ids: string[]): void {
    const items = Array.isArray(element.items) ? element.items : [];
    const displayField = element.getAttribute('display-field') || 'value';

    element.value = ids.map((id) => {
        const match = items.find((item) => String(item.id) === id);
        if (match) {
            const record = match as Record<string, unknown>;
            return {
                id: String(match.id),
                value: String(record[displayField] ?? match.value ?? match.id),
            };
        }
        return {id, value: id};
    });

    // Keep attribute in sync — never removeAttribute('value') (Lit nulls Arrays).
    syncCbarMultiSelectValueAttribute(element);
    element._updateAvailableItems?.();
    // Prefer the component dispatcher so value-changed listeners run.
    if (typeof element._dispatchValueChanged === 'function') {
        element._dispatchValueChanged();
    } else if (typeof element.dispatchEvent === 'function') {
        element.dispatchEvent(
            new CustomEvent('value-changed', {
                bubbles: true,
                composed: true,
                detail: {value: element.value},
            }),
        );
    }
}

/**
 * Material input chips dispatch a non-composed `remove`, then call
 * `HTMLElement.remove()` on themselves when not cancelled. Parents never see
 * the event, `value` stays stale, and the chip looks gone until the next
 * render re-adds it. Intercept the trailing-X click on every multi-select.
 */
function wireCbarMultiSelectChipRemoveFix(element: CbarMultiSelectElement): void {
    if (element.hasAttribute(CBAR_MULTI_SELECT_CHIP_REMOVE_ATTR)) {
        return;
    }

    element.setAttribute(CBAR_MULTI_SELECT_CHIP_REMOVE_ATTR, '');
    element.addEventListener(
        'click',
        (event: Event) => {
            const mouseEvent = event as MouseEvent;
            if (typeof mouseEvent.button === 'number' && mouseEvent.button !== 0) {
                return;
            }

            const path = event.composedPath();
            const removeButton = path.find((node): node is HTMLElement => {
                const candidate = node as HTMLElement;
                return (
                    !!candidate
                    && typeof candidate === 'object'
                    && candidate.tagName === 'BUTTON'
                    && typeof candidate.classList?.contains === 'function'
                    && candidate.classList.contains('trailing')
                );
            });
            if (!removeButton) {
                return;
            }

            const chip = path.find((node): node is HTMLElement => {
                const candidate = node as HTMLElement;
                return (
                    !!candidate
                    && typeof candidate === 'object'
                    && candidate.tagName === 'CBAR-CHIP'
                );
            });
            if (!chip) {
                return;
            }

            const label = chip.textContent?.trim() ?? '';
            if (!label) {
                return;
            }

            const items = Array.isArray(element.items) ? element.items : [];
            const displayField = element.getAttribute('display-field') || 'value';
            const searchProp = element.searchProp || element.getAttribute('search-prop') || 'id';
            const match = items.find((item) => {
                const record = item as Record<string, unknown>;
                return (
                    String(record[displayField] ?? item.value ?? '') === label
                    || String(item.id) === label
                );
            });
            const removeId = match
                ? String(match.id)
                : getCbarMultiSelectIdsFromElement(element).find((id) => {
                    const item = items.find((candidate) => String(candidate.id) === id);
                    return item && String(item.value) === label;
                });

            if (!removeId) {
                return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            const nextIds = getCbarMultiSelectIdsFromElement(element).filter(
                (id) => String(id) !== String(removeId),
            );
            // Also drop by search-prop against live value in case id extraction lagged.
            const fromValue = (Array.isArray(element.value) ? element.value : [])
                .map((entry) => resolveMultiSelectEntryId(entry, searchProp))
                .filter((id) => id !== '' && id !== String(removeId));
            const ids = nextIds.length <= fromValue.length ? nextIds : fromValue;

            writeMultiSelectIds(element, [...new Set(ids)]);
        },
        true,
    );
}

/** Keep the live `value` property and attribute aligned after programmatic sets. */
export function setCbarMultiSelectValue(
    element: CbarMultiSelectElement,
    value: Array<{id: string; value?: string; title?: string} | string>,
): void {
    element.value = value.map((entry) => {
        if (typeof entry === 'string') {
            return toMultiSelectItem(entry, entry);
        }
        const label = String(entry.value ?? entry.title ?? entry.id);
        return toMultiSelectItem(String(entry.id), label, String(entry.title ?? label));
    });
    syncCbarMultiSelectValueAttribute(element);
    syncCbarMultiSelectFormValue(element);
}

function wireCbarMultiSelectValueSync(element: CbarMultiSelectElement): void {
    if (element.hasAttribute(CBAR_MULTI_SELECT_VALUE_SYNC_ATTR)) {
        return;
    }

    element.setAttribute(CBAR_MULTI_SELECT_VALUE_SYNC_ATTR, '');
    patchCbarMultiSelectRemoveItem(element);
    wireCbarMultiSelectChipRemoveFix(element);
    element.addEventListener('value-changed', () => {
        syncCbarMultiSelectValueAttribute(element);
        syncCbarMultiSelectFormValue(element);
    });
    wireFilterMultiSelectCloseOnSelect(element);
}

function syncCbarMultiSelectFormValue(element: CbarMultiSelectElement): void {
    const name = element.name || element.getAttribute('name') || '';
    if (!name || !element._internals) {
        return;
    }

    const current = Array.isArray(element.value) ? element.value : [];
    if (current.length === 0) {
        element._internals.setFormValue(null);
        return;
    }

    const formData = new FormData();
    getCbarMultiSelectIdsFromElement(element).forEach((id) => {
        formData.append(name, id);
    });
    element._internals.setFormValue(formData);
}

/** Copy server-rendered value="[{id,value}]" into the component property. */
export function hydrateCbarMultiSelect(element: CbarMultiSelectElement): void {
    ensureInitialMultiSelectItemsCached(element);

    if (element.hasAttribute(CBAR_MULTI_SELECT_HYDRATED_ATTR)) {
        syncCbarMultiSelectItemsFromAttribute(element);
        // Re-apply runtime patches when hydrate runs again after dynamic inserts.
        patchCbarMultiSelectRemoveItem(element);
        wireCbarMultiSelectChipRemoveFix(element);
        return;
    }

    const items = readInitialMultiSelectItems(element);
    if (shouldReplaceMultiSelectItems(normalizeMultiSelectItemsProperty(element.items), items)) {
        element.items = items;
    }

    const current = Array.isArray(element.value) ? element.value : [];
    if (current.length === 0) {
        const initial = readInitialMultiSelectValue(element);
        if (initial.length > 0) {
            element.value = filterMultiSelectValueToItems(initial, items);
        }
    } else {
        element.value = filterMultiSelectValueToItems(
            current.map((entry) => normalizeMultiSelectValueEntry(entry, items)),
            items,
        );
    }

    syncCbarMultiSelectValueAttribute(element);
    patchCbarMultiSelectRemoveItem(element);
    wireCbarMultiSelectValueSync(element);
    wireFilterMultiSelectCloseOnSelect(element);
    syncCbarMultiSelectFormValue(element);
    element.setAttribute(CBAR_MULTI_SELECT_HYDRATED_ATTR, '');
}

export function hydrateCbarMultiSelectsIn(root: ParentNode = document): void {
    root.querySelectorAll('cbar-multi-select-combo-box, cbar-multi-select').forEach((node) => {
        if (isCbarMultiSelect(node)) {
            hydrateCbarMultiSelect(node);
        }
    });
}

/** Stop form-associated multi-selects from also submitting JSON form values. */
function clearCbarMultiSelectFormAssociation(element: CbarMultiSelectElement): void {
    element.removeAttribute('name');
    element.name = '';
    element._internals?.setFormValue(null);
}

function restoreCbarMultiSelectFormAssociation(element: CbarMultiSelectElement, name: string): void {
    element.setAttribute('name', name);
    element.name = name;
    syncCbarMultiSelectFormValue(element);
}

function hiddenInputsForMultiSelectField(
    form: HTMLFormElement,
    name: string,
): HTMLInputElement[] {
    const selector = `input[type="hidden"][${CBAR_MULTI_SELECT_HIDDEN_ATTR}="${escapeSelectorValue(name)}"]`;
    return Array.from(form.querySelectorAll(selector)) as HTMLInputElement[];
}

/**
 * Replace JSON object form values from cbar-multi-select with primitive tag ids
 * before the browser (or FormData) serializes the form.
 */
export function applyCbarMultiSelectSubmitValues(form: HTMLFormElement): void {
    form.querySelectorAll('cbar-multi-select-combo-box[name], cbar-multi-select[name]').forEach((node) => {
        if (!isCbarMultiSelect(node)) {
            return;
        }

        const name = node.getAttribute('name');
        if (!name) {
            return;
        }

        hiddenInputsForMultiSelectField(form, name).forEach((hidden) => hidden.remove());

        const ids = getCbarMultiSelectIdsFromElement(node);
        ids.forEach((id) => {
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = name;
            hidden.value = id;
            hidden.setAttribute(CBAR_MULTI_SELECT_HIDDEN_ATTR, name);
            form.appendChild(hidden);
        });

        if (!node.hasAttribute(CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR)) {
            node.setAttribute(CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR, name);
            clearCbarMultiSelectFormAssociation(node);
        }
    });
}

export function restoreCbarMultiSelectNames(form: HTMLFormElement): void {
    form.querySelectorAll(
        `cbar-multi-select-combo-box[${CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR}], cbar-multi-select[${CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR}]`,
    ).forEach((node) => {
        const name = node.getAttribute(CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR);
        if (!name) {
            return;
        }

        if (isCbarMultiSelect(node)) {
            restoreCbarMultiSelectFormAssociation(node, name);
        }
        node.removeAttribute(CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR);
    });

    form.querySelectorAll(`input[type="hidden"][${CBAR_MULTI_SELECT_HIDDEN_ATTR}]`).forEach((node) => {
        node.parentNode?.removeChild(node);
    });
}

export async function waitForCbarMultiSelectsReady(root: ParentNode = document): Promise<void> {
    cacheCbarMultiSelectItemsAttributesIn(root);
    await customElements.whenDefined('cbar-multi-select-combo-box');
    cacheCbarMultiSelectItemsAttributesIn(root);

    const elements = Array.from(
        root.querySelectorAll('cbar-multi-select-combo-box, cbar-multi-select'),
    ) as LitElementLike[];

    await Promise.all(
        elements.map((element) => element.updateComplete ?? Promise.resolve(true)),
    );

    hydrateCbarMultiSelectsIn(root);

    await Promise.all(
        elements.map((element) => element.updateComplete ?? Promise.resolve(true)),
    );

    elements.forEach((element) => {
        if (isCbarMultiSelect(element)) {
            syncCbarMultiSelectItemsFromAttribute(element);
        }
    });
}

export async function setupCbarMultiSelectHydration(): Promise<void> {
    setupExclusiveMultiSelectDismiss();
    cacheCbarMultiSelectItemsAttributesIn(document);
    await customElements.whenDefined('cbar-multi-select-combo-box');
    cacheCbarMultiSelectItemsAttributesIn(document);

    const run = () => {
        cacheCbarMultiSelectItemsAttributesIn(document);
        hydrateCbarMultiSelectsIn(document);
    };
    run();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, {once: true});
    }

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) {
                    return;
                }

                if (isCbarMultiSelect(node)) {
                    ensureInitialMultiSelectItemsCached(node);
                    hydrateCbarMultiSelect(node);
                    return;
                }

                cacheCbarMultiSelectItemsAttributesIn(node);
                hydrateCbarMultiSelectsIn(node);
            });
        }
    });

    observer.observe(document.documentElement, {childList: true, subtree: true});
}
